import { Key, ItemValue } from './types'

export type Item<T = ItemValue> = {
  /** Resolved key for item identification. */
  key: Key,
  /** Item initial value. */
  value: T,
}
export type ItemsIndexMap = Record<Key, number>

function toValue<T = ItemValue>(item: Item<T>): T {
  return item.value
}

/**
 * Special elements container that stores items as list with a
 * simple interface for fast item lookup by it's key.
 *
 * **Example:**
 * ```js
 * import { ItemsMap } from '@aspectus/selection-controller'
 *
 * const items = [
 *   { key: 1, value: { id: 1, title: 'First' } },
 *   { key: 2, value: { id: 2, title: 'Second' } },
 *   { key: 3, value: { id: 3, title: 'Third' } },
 * ]
 * const itemsMap = new ItemsMap(items)
 *
 * // You may iterate using `itemsMap.items` because
 * // they are equals to items.
 * // And to get a specific one fast you may use `.get` method:
 * console.log(itemsMap.get(2))
 * // > { key: 2, value: { id: 2, title: 'Second' } }
 * ```
 *
 * @export
 * @class ItemsMap
 */
export class ItemsMap<T = ItemValue> {
  items: Item<T>[]
  indexMap: ItemsIndexMap

  /**
   * Stores passed items and generates special indexing map.
   * @param items - List of items to store.
   * @memberof ItemsMap
   */
  constructor(items: Item<T>[]) {
    this.items = items
    this.indexMap = this.items.reduce((acc: ItemsIndexMap, { key }, i) => {
      acc[key] = i

      return acc
    }, {})
  }

  /**
   * Gets item by it's key.
   *
   * @param key - Item key to search for an item.
   * @returns Resolved item or `undefined` if not exists.
   * @memberof ItemsMap
   */
  get(key: Key): Item<T> {
    return this.items[this.indexMap[key]]
  }

  /**
   * Returns raw items values list.
   *
   * @returns Items values list.
   * @memberof ItemsMap
   */
  raw(): T[] {
    return this.items.map(toValue)
  }
}
